<template>
	<div class="schedule">
		<header class="schedule-header">
			<button class="back-button" @click="goBack" />
			<span class="title">
				Operating Hours
			</span>
			<div />
		</header>
		<hours-switcher
			:switcher-labels="filteredHoursTypes"
			:active-tab="scheduleType"
			@handle-change="switchScheduleType"
		/>
		<div class="schedule-content">
			<ul class="schedule-list">
				<li
					v-for="(day, i) in weekDays"
					class="schedule-list-item"
					:class="{
						active: i === activeDayIndex,
						'without-border': getIsWithoutBorder(i)
					}"
					:key="i"
				>
					<span class="schedule-list-day">{{ day.long }}</span>
					<ul class="schedule-time">
						<template v-if="!getHours(i).length">
							<li>
								{{ scheduleType === 'Open' ? 'Closed' : 'Non Accessible' }}
							</li>
						</template>
						<template v-else>
							<li v-for="(hours, y) in getHours(i)" :key="y">
								{{ callGetAmPmTime(hours.startTime) }}
								-
								{{ callGetAmPmTime(hours.endTime) }}
							</li>
						</template>
					</ul>
				</li>
			</ul>
			<div class="schedule-notes" v-html="currentNote" />
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
// Utils
import { getAmPmTime, getDateNow, getCleanHTML } from '@/utils/helpers'
// Components
import HoursSwitcher from '@/components/HoursSwitcher'
// Constants
import { TIME_FORMATS, WEEK_DAYS } from '@/constants'

const SCHEDULE_TYPES = {
	open: 'Open',
	forHere: 'Dine In',
	pickup: 'Pickup'
	// forHere: 'For Here',
	// toGo: 'To Go',
}

const SCHEDULE_NOTES = {
	Open: 'noteOpenHours',
	'Dine In': 'noteForHereHours',
	'To Go': 'noteToGoHours',
	Pickup: 'notePickUpHours'
}

const newHoursTypes = Object.values(SCHEDULE_TYPES)

export default {
	name: 'Schedule',

	components: {
		HoursSwitcher
	},

	data() {
		return {
			scheduleType: 'Open',
			isMobileView: false,
			header: null
		}
	},

	mounted() {
		window.addEventListener('resize', this.onResize)
		this.onResize()

		this.header = document.querySelector('.schedule-header')

		this.setCurrentHoursType()
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.onResize)
	},

	computed: {
		...mapState('venue', ['venueInfo']),

		currentNote() {
			const currentNoteKey = SCHEDULE_NOTES[this.scheduleType]
			const noteHTML = currentNoteKey
				? this.venueInfo.settings[currentNoteKey]
				: ''

			return getCleanHTML(noteHTML)
		},

		filteredHoursTypes() {
			const types = [...newHoursTypes]

			if (!this.venueInfo.acceptForHere) {
				const forHereIndex = types.findIndex(
					type => type === SCHEDULE_TYPES.forHere
				)
				types.splice(forHereIndex, 1)
			}

			if (!this.venueInfo.acceptPickUp) {
				const pickupIndex = types.findIndex(
					type => type === SCHEDULE_TYPES.pickup
				)
				types.splice(pickupIndex, 1)
			}

			this.switchScheduleType(types[0])

			return types
		},

		weekDays() {
			return WEEK_DAYS
		},

		activeDayIndex() {
			return getDateNow(this.venueInfo.timeZone).day()
		},

		schedule() {
			const venueInfo = this.venueInfo

			switch (this.scheduleType) {
				case SCHEDULE_TYPES.toGo:
					return venueInfo.toGoHours
				case SCHEDULE_TYPES.forHere:
					return venueInfo.forHereHours
				case SCHEDULE_TYPES.pickup:
					return venueInfo.pickupHours
				default:
					return venueInfo.openHours
			}
		}
	},

	methods: {
		async goBack() {
			const params = this.$route.query.category
				? { category: this.$route.query.category }
				: {}
			const query = { ...this.$route.query }

			if (query.category) {
				const { isFromCart } = query

				if (!isFromCart) delete query.category

				delete query.selected
				delete query.isFromCart

				return await this.$router.push({
					name: isFromCart ? 'cart' : 'products-category',
					params: isFromCart ? {} : params,
					query
				})
			}

			await this.$router.push({ name: 'products' })
		},

		getIsWithoutBorder(index) {
			return this.activeDayIndex !== 0 && index === this.activeDayIndex - 1
		},

		setCurrentHoursType() {
			const { selected } = this.$route.query

			if (selected && this.isCurrentTypeAccepted(selected)) {
				this.scheduleType = selected
			}
		},

		isCurrentTypeAccepted(selected) {
			switch (selected) {
				case SCHEDULE_TYPES.toGo:
					return this.venueInfo.acceptToGo
				case SCHEDULE_TYPES.forHere:
					return this.venueInfo.acceptForHere
				case SCHEDULE_TYPES.pickup:
					return this.venueInfo.acceptPickUp
				default:
					return false
			}
		},

		onResize() {
			const innerWidth = window.innerWidth

			if (innerWidth > 480 && this.isMobileView) {
				this.isMobileView = false
			}

			if (innerWidth <= 480 && !this.isMobileView) {
				this.isMobileView = true
			}
		},

		switchScheduleType(val) {
			this.scheduleType = val

			this.$nextTick(() => {
				if (!this.header) return

				this.header.scrollIntoView({ behavior: 'smooth' })
			})
		},

		callGetAmPmTime(time) {
			return getAmPmTime(time, TIME_FORMATS.AMPM)
		},

		getHours(dayIndex) {
			return (
				(this.schedule &&
					this.schedule.filter(
						item => item.dow === dayIndex + 1 && !item.closed
					)) ||
				[]
			)
		}
	}
}
</script>

<style lang="scss" scoped>
@import './src/assets/styles/variables';

.schedule {
	width: 100%;
}

.schedule-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-height: 50px;
	margin: 0 0 22px;
	flex-shrink: 0;
	padding: 4px 12px;
	color: $white;
	background-color: $grey-dark;

	.title {
		display: block;
		font-size: 20px;
		font-weight: 700;
		text-align: center;
	}

	.sub-title {
		display: block;
		font-size: 12px;
		opacity: 0.5;
	}
}

.schedule-content {
	margin: 0 auto;
	min-height: calc(100vh - 141px);
	display: flex;
	flex-direction: column;
	max-width: 600px;
}

.schedule-notes {
	padding: 0 16px;
	word-wrap: break-word;
}

.schedule-list {
	width: 100%;
	margin: 0 auto 24px auto;
	padding: 0;
	color: $white;
	list-style: none;
}

.schedule-list-item {
	position: relative;
	padding: 14px 16px;
	overflow: hidden;

	&:after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 14px;
		right: 14px;
		border-bottom: 1px solid rgba($white, 0.05);
	}

	&.active {
		background: $blue-faceless-void;
		border: 0;
		border-radius: 10px;

		.schedule-time li {
			color: $white;
		}
	}

	&.without-border,
	&.active {
		&:after {
			display: none;
		}
	}
}

.schedule-list-day {
	float: left;
	font-size: 16px;
	font-weight: 700;
}

.schedule-time {
	float: right;
	margin: 0;
	padding: 2px 0 0;
	list-style: none;
	text-align: right;

	li {
		padding: 0 0 4px;
		color: rgba(255, 255, 255, 0.5);

		&:last-child {
			padding: 0;
		}
	}
}

@media (max-width: 767px) {
	.schedule-header .title {
		font-size: 16px;
		line-height: 1.5;
	}

	.schedule-list {
		padding: 0 8px;
	}

	.schedule-notes {
		padding: 0 24px;
	}

	.schedule-content {
		min-height: calc(100vh - 200px);
	}
}
</style>
